import {QuestionStatus} from '@cp/model';
import {useCallback, useEffect, useState} from 'react';
import countQuestionsByFiltersDoc from '../../api/question/countQuestionsByFiltersDoc';

export interface CountQuestionsVariables {
  moduleId: string;
  topics?: string[];
  difficulties?: number[];
  statuses?: QuestionStatus[];
}

export function useCountQuestions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [count, setCount] = useState<number>(0);

  const countQuestions = useCallback(async (variables: CountQuestionsVariables) => {
    setLoading(true);
    setError(undefined);
    try {
      const n = await countQuestionsByFiltersDoc({
        moduleId: variables.moduleId,
        topics: variables.topics,
        difficulties: variables.difficulties,
        statuses: variables.statuses,
      });
      setCount(n);
      return n;
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    countQuestions,
    loading,
    error,
    count,
  };
}

/**
 * Fetch count automatically when parameter updated
 * @param moduleId
 * @param topics
 * @param difficulties
 * @param statuses
 * @returns undefined without module, number of questions otherwise
 */
export function useCountQuestionsFetch(
  moduleId?: string,
  topics?: string[],
  difficulties?: number[],
  statuses?: QuestionStatus[],
) {
  const {countQuestions} = useCountQuestions();
  const [count, setCount] = useState<number | undefined>(undefined);

  const topicsKey = topics?.join(',');
  const difficultiesKey = difficulties?.join(',');
  const statusesKey = statuses?.join(',');

  useEffect(() => {
    let isCancelled = false;

    if (moduleId) {
      countQuestions({moduleId, topics, difficulties, statuses})
        .then((result) => {
          if (!isCancelled) {
            setCount(result);
          }
        })
        .catch((err) => {
          if (err.name !== 'AbortError' && !isCancelled) {
            console.error('Error fetching question count:', err);
          }
        });
    } else {
      setCount(undefined);
    }

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId, topicsKey, difficultiesKey, statusesKey]);

  return count;
}
